import { useEffect, useState } from "react";
import { useNavigate } from "react-router-dom";
import { supabase } from "@/lib/supabase";
import { Wordmark } from "@/components/Wordmark";
import { brandFromLocation, brandMarkSrc } from "@/lib/brand";

type Step = "loading" | "set-password" | "error";
type Flow = "invite" | "recovery";

export function AuthCallback(): React.JSX.Element {
  // Anonymous by definition — the user is setting a password, so there is no
  // client yet. Hostname decides; unknown hosts get the platform brand.
  const brand = brandFromLocation();
  const navigate = useNavigate();
  const [step, setStep] = useState<Step>("loading");
  const [flow, setFlow] = useState<Flow>("invite");
  const [password, setPassword] = useState("");
  const [confirmPassword, setConfirmPassword] = useState("");
  const [submitting, setSubmitting] = useState(false);
  const [error, setError] = useState<string | null>(null);

  useEffect(() => {
    // Supabase encodes errors in the hash fragment when a link is expired/invalid
    const hash = new URLSearchParams(window.location.hash.slice(1));
    if (hash.get("error")) {
      setStep("error");
      return;
    }

    const code = new URLSearchParams(window.location.search).get("code");

    if (code) {
      const type = new URLSearchParams(window.location.search).get("type");
      if (type === "recovery") setFlow("recovery");
      supabase.auth.exchangeCodeForSession(code).then(({ error: err }) => {
        if (err) setStep("error");
        else setStep("set-password");
      });
      return;
    }

    // Hash-based (implicit) flow — Supabase client parses the fragment automatically
    const { data: { subscription } } = supabase.auth.onAuthStateChange((event) => {
      if (event === "PASSWORD_RECOVERY") {
        setFlow("recovery");
        setStep("set-password");
        subscription.unsubscribe();
      } else if (event === "SIGNED_IN" || event === "USER_UPDATED") {
        setStep("set-password");
        subscription.unsubscribe();
      }
    });

    return () => subscription.unsubscribe();
  }, []);

  async function handleSubmit(e: React.FormEvent<HTMLFormElement>): Promise<void> {
    e.preventDefault();
    if (password !== confirmPassword) {
      setError("Las contraseñas no coinciden.");
      return;
    }
    setSubmitting(true);
    setError(null);
    const { error: updateError } = await supabase.auth.updateUser({ password });
    if (updateError) {
      setError(updateError.message);
      setSubmitting(false);
      return;
    }
    navigate("/", { replace: true });
  }

  const inputClass =
    "w-full rounded-[14px] border-[1.5px] border-ink-100 px-[14px] py-[13px] text-[15px] text-ink-900 bg-white outline-none transition focus:border-moss placeholder:text-ink-400";

  return (
    <div className="flex h-[100dvh] flex-col items-center justify-center bg-ink-050 px-4 pb-12 overflow-y-auto">
      <div className="mb-8 text-center">
        <div className="inline-flex items-center gap-2">
          <img src={brandMarkSrc(brand)} alt="" className="h-8 w-8 flex-shrink-0" />
          <Wordmark brand={brand} className="text-[22px]" />
        </div>
        <p className="mt-1 text-[12px] text-ink-400">CRM de Admisiones</p>
      </div>

      <div className="w-full max-w-sm rounded-[14px] bg-white p-6 shadow-sm border border-ink-100">
        {step === "loading" && (
          <p className="text-sm text-ink-500 text-center">Verificando invitación…</p>
        )}

        {step === "error" && (
          <>
            <h1 className="font-display text-[22px] font-bold text-ink-900 mb-2">
              Enlace inválido
            </h1>
            <p className="text-[13px] text-ink-500">
              El enlace expiró o ya fue usado. Pide al administrador que te envíe uno nuevo.
            </p>
          </>
        )}

        {step === "set-password" && (
          <>
            <h1 className="font-display text-[22px] font-bold text-ink-900 mb-1">
              {flow === "recovery" ? "Nueva contraseña" : "Crea tu contraseña"}
            </h1>
            <p className="mb-6 text-[13px] text-ink-500">
              {flow === "recovery"
                ? "Elige una contraseña nueva para tu cuenta."
                : "Elige una contraseña para activar tu cuenta."}
            </p>

            <form onSubmit={handleSubmit} className="flex flex-col gap-4">
              <div className="flex flex-col gap-[6px]">
                <label htmlFor="password" className="text-[12px] font-semibold text-ink-700">
                  Contraseña
                </label>
                <input
                  id="password"
                  type="password"
                  value={password}
                  onChange={(e) => setPassword(e.target.value)}
                  placeholder="Mínimo 8 caracteres"
                  minLength={8}
                  required
                  autoFocus
                  className={inputClass}
                />
              </div>

              <div className="flex flex-col gap-[6px]">
                <label htmlFor="confirm-password" className="text-[12px] font-semibold text-ink-700">
                  Confirmar contraseña
                </label>
                <input
                  id="confirm-password"
                  type="password"
                  value={confirmPassword}
                  onChange={(e) => setConfirmPassword(e.target.value)}
                  placeholder="Repite tu contraseña"
                  minLength={8}
                  required
                  className={inputClass}
                />
              </div>

              {error !== null && (
                <div className="text-[12px] text-score-hot-fg bg-score-hot-bg px-3 py-2.5 rounded-[6px] leading-snug">
                  {error}
                </div>
              )}

              <button
                type="submit"
                disabled={submitting}
                className="w-full py-[14px] bg-lime text-moss rounded-[14px] text-[15px] font-semibold mt-1 disabled:opacity-60 transition-opacity cursor-pointer"
              >
                {submitting ? "Guardando…" : "Activar cuenta"}
              </button>
            </form>
          </>
        )}
      </div>
    </div>
  );
}
